import { useState } from "react";

import { type ApiError, toApiError } from "../../api/client";
import { getResult, reviewResult } from "../../api/freightAudit";
import { ReviewBadge, StatusBadge } from "../../components/Badges";
import { Button } from "../../components/Button";
import { Drawer } from "../../components/Drawer";
import { ErrorNotice, Skeleton } from "../../components/Feedback";
import { Icon } from "../../components/Icon";
import { useAsync } from "../../hooks/useAsync";
import {
  formatDate,
  formatDateTime,
  formatMoney,
  formatSignedMoney,
  formatSignedWeight,
  formatWeight,
  varianceTone,
} from "../../lib/format";
import type { AuditResultDetail, ReviewState } from "../../types/api";

export interface ReviewDraft {
  review_state: ReviewState;
  note: string;
}

const DECISIONS: { state: ReviewState; label: string }[] = [
  { state: "APPROVED", label: "Approve" },
  { state: "DISPUTE", label: "Mark for dispute" },
  { state: "IGNORED", label: "Ignore" },
];

const SHIPMENT_FIELDS: { key: string; label: string }[] = [
  { key: "bol_number", label: "BOL" },
  { key: "po_number", label: "PO" },
  { key: "carrier", label: "Carrier" },
  { key: "ship_date", label: "Ship date" },
];

interface AuditDetailProps {
  auditId: string;
  resultId: string;
  position: { index: number; total: number } | null;
  draft: ReviewDraft | undefined;
  onDraftChange: (draft: ReviewDraft | null) => void;
  onSaved: (detail: AuditResultDetail) => void;
  onPrevious: (() => void) | null;
  onNext: (() => void) | null;
  onClose: () => void;
}

function Comparison({ detail }: { detail: AuditResultDetail }) {
  const orphan = detail.quoted_total === null;
  return (
    <section className="detail-section" aria-labelledby="detail-compare">
      <h3 id="detail-compare">Expected vs billed</h3>
      <table className="compare">
        <thead>
          <tr>
            <th scope="col">
              <span className="visually-hidden">Measure</span>
            </th>
            <th scope="col">Expected</th>
            <th scope="col">Billed</th>
            <th scope="col">Variance</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row">Total</th>
            <td>{formatMoney(detail.quoted_total)}</td>
            <td>{formatMoney(detail.billed_total)}</td>
            <td className={`variance variance--${varianceTone(detail.total_variance)}`}>
              {formatSignedMoney(detail.total_variance)}
            </td>
          </tr>
          <tr>
            <th scope="row">Weight</th>
            <td>{formatWeight(detail.quoted_weight)}</td>
            <td>{formatWeight(detail.billed_weight)}</td>
            <td className={`variance variance--${varianceTone(detail.weight_variance)}`}>
              {formatSignedWeight(detail.weight_variance)}
            </td>
          </tr>
        </tbody>
      </table>
      {orphan ? <p className="field-hint">No shipment record, so there is no quote to compare against.</p> : null}
    </section>
  );
}

function Flags({ detail }: { detail: AuditResultDetail }) {
  return (
    <section className="detail-section" aria-labelledby="detail-flags">
      <h3 id="detail-flags">Flags</h3>
      {detail.reasons.length ? (
        <ul className="flag-list">
          {detail.reasons.map((reason) => (
            <li key={reason.code} className={`flag flag--${reason.status.toLowerCase()}`}>
              <Icon name="alert" />
              <span>{reason.message}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="flag flag--pass">
          <Icon name="check" /> All checks passed within tolerance.
        </p>
      )}
    </section>
  );
}

function Accessorials({ detail }: { detail: AuditResultDetail }) {
  if (!detail.accessorials.length) return null;
  return (
    <section className="detail-section" aria-labelledby="detail-accessorials">
      <h3 id="detail-accessorials">Accessorials</h3>
      <table className="mini-table">
        <thead>
          <tr>
            <th scope="col">Charge</th>
            <th scope="col">Amount</th>
            <th scope="col">Approved at tender</th>
          </tr>
        </thead>
        <tbody>
          {detail.accessorials.map((charge, index) => (
            <tr key={`${charge.type}-${index}`} className={charge.flagged ? "row--flagged" : undefined}>
              <td>
                {charge.label}
                {charge.flagged ? <span className="tag tag--review">Unexpected</span> : null}
              </td>
              <td className="numeric">{formatMoney(charge.amount)}</td>
              <td>{charge.approved === null ? "No shipment" : charge.approved ? "Yes" : "No"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function ShipmentContext({ detail }: { detail: AuditResultDetail }) {
  const shipment = detail.shipment;
  if (!shipment) return null;
  const lane = [shipment.origin, shipment.destination].filter(Boolean).join(" → ");
  return (
    <section className="detail-section" aria-labelledby="detail-shipment">
      <h3 id="detail-shipment">Shipment</h3>
      <dl className="kv">
        {SHIPMENT_FIELDS.filter((field) => shipment[field.key]).map((field) => (
          <div key={field.key}>
            <dt>{field.label}</dt>
            <dd>{field.key === "ship_date" ? formatDate(shipment[field.key]) : shipment[field.key]}</dd>
          </div>
        ))}
        {lane ? (
          <div>
            <dt>Lane</dt>
            <dd>{lane}</dd>
          </div>
        ) : null}
      </dl>
    </section>
  );
}

interface ReviewFormProps {
  auditId: string;
  detail: AuditResultDetail;
  draft: ReviewDraft | undefined;
  onDraftChange: (draft: ReviewDraft | null) => void;
  onSaved: (detail: AuditResultDetail) => void;
  onNext: (() => void) | null;
}

function ReviewForm({ auditId, detail, draft, onDraftChange, onSaved, onNext }: ReviewFormProps) {
  const [decision, setDecision] = useState<ReviewState>(draft?.review_state ?? detail.review_state);
  const [note, setNote] = useState(draft?.note ?? detail.review_note ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const dirty = decision !== detail.review_state || note.trim() !== (detail.review_note ?? "");

  function change(next: ReviewDraft) {
    setDecision(next.review_state);
    setNote(next.note);
    setSavedAt(null);
    const unchanged = next.review_state === detail.review_state && next.note.trim() === (detail.review_note ?? "");
    onDraftChange(unchanged ? null : next);
  }

  async function save(thenNext: boolean) {
    setSaving(true);
    setError(null);
    try {
      const saved = await reviewResult(auditId, detail.id, { review_state: decision, note: note.trim() || null });
      onDraftChange(null);
      onSaved(saved);
      setSavedAt(saved.reviewed_at);
      if (thenNext && onNext) onNext();
    } catch (caught) {
      setError(toApiError(caught));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="detail-section review-form" aria-labelledby="detail-review">
      <h3 id="detail-review">Review decision</h3>
      <div className="decision-group" role="radiogroup" aria-labelledby="detail-review">
        {DECISIONS.map((option) => (
          <button
            key={option.state}
            type="button"
            role="radio"
            aria-checked={decision === option.state}
            className={`decision decision--${option.state.toLowerCase()}${decision === option.state ? " is-selected" : ""}`}
            onClick={() => change({ review_state: decision === option.state ? "UNREVIEWED" : option.state, note })}
          >
            {option.label}
          </button>
        ))}
      </div>
      <label className="field-label" htmlFor="review-note">
        Note
      </label>
      <textarea
        id="review-note"
        rows={3}
        maxLength={2000}
        value={note}
        placeholder="e.g. Carrier reweighed the shipment; scale ticket confirms 557 lb."
        onChange={(event) => change({ review_state: decision, note: event.target.value })}
      />
      {error ? <ErrorNotice error={error} compact /> : null}
      <div className="review-form__actions">
        <span className="review-form__status" aria-live="polite">
          {dirty ? "Unsaved changes" : savedAt ? `Saved ${formatDateTime(savedAt)}` : null}
        </span>
        {onNext ? (
          <Button onClick={() => save(true)} disabled={!dirty} busy={saving}>
            Save &amp; next
          </Button>
        ) : null}
        <Button variant="primary" onClick={() => save(false)} disabled={!dirty} busy={saving}>
          Save
        </Button>
      </div>
      <p className="field-hint">FreightAudit records your decision. It never files a dispute or pays an invoice.</p>
    </section>
  );
}

export function AuditDetail(props: AuditDetailProps) {
  const { auditId, resultId, position, draft, onDraftChange, onSaved, onPrevious, onNext, onClose } = props;
  const detail = useAsync((signal) => getResult(auditId, resultId, signal), resultId);
  const data = detail.data?.id === resultId ? detail.data : undefined;

  return (
    <Drawer onClose={onClose} labelledBy="detail-title">
      <header className="drawer__header">
        <div>
          <p className="eyebrow">{position ? `Row ${position.index + 1} of ${position.total}` : "Invoice"}</p>
          <h2 id="detail-title">{data ? `Invoice ${data.invoice_number || "(blank)"}` : "Loading invoice…"}</h2>
          {data ? <p className="muted mono">PRO {data.pro_number || "(blank)"}</p> : null}
        </div>
        <div className="drawer__nav">
          <Button variant="ghost" size="sm" icon="chevron-left" onClick={onPrevious ?? undefined} disabled={!onPrevious} aria-label="Previous invoice" />
          <Button variant="ghost" size="sm" icon="chevron-right" onClick={onNext ?? undefined} disabled={!onNext} aria-label="Next invoice" />
          <Button variant="ghost" size="sm" icon="x" onClick={onClose} aria-label="Close" />
        </div>
      </header>
      <div className="drawer__body">
        {detail.error ? <ErrorNotice error={detail.error} onRetry={detail.reload} /> : null}
        {!data && !detail.error ? <Skeleton lines={8} label="Loading invoice" /> : null}
        {data ? (
          <>
            <div className="detail-status">
              <StatusBadge status={data.status} />
              <ReviewBadge state={data.review_state} />
              {data.carrier ? <span className="muted">{data.carrier}</span> : null}
            </div>
            <Comparison detail={data} />
            <Flags detail={data} />
            <Accessorials detail={data} />
            <ShipmentContext detail={data} />
            <ReviewForm
              key={data.id}
              auditId={auditId}
              detail={data}
              draft={draft}
              onDraftChange={onDraftChange}
              onSaved={(saved) => {
                detail.update(() => saved);
                onSaved(saved);
              }}
              onNext={onNext}
            />
            <p className="detail-meta">
              Invoice row {data.source_row ?? "—"}
              {data.shipment_source_row ? ` · Shipment row ${data.shipment_source_row}` : ""}
              {data.invoice_date ? ` · Invoiced ${formatDate(data.invoice_date)}` : ""}
            </p>
          </>
        ) : null}
      </div>
    </Drawer>
  );
}
